import { SITE_URL, SITE_NAME } from "@/lib/seo";

/**
 * Structured data (JSON-LD). Strings are Witherspoon-CLO's, verbatim, from
 * "AIOpCo Docs & Templates/legal/SEO_Copy_Rulings.md" Section 1 (2026-10-05). Do not reword here.
 *
 * Rules (CLO Section 1): no "compare", "comparison", "plan", "provider", "shopper" in any JSON-LD string;
 * no audience, foundingDate, sameAs, aggregateRating, review, award or slogan; no `Service` node (deleted).
 *  - `legalName: "RateCurrent LLC"` is added ONLY after the footer shows "RateCurrent LLC" and the Owner
 *    confirms the Texas SOS filing was accepted.
 *  - `logo` stays the favicon until a 512px PNG exists and returns 200.
 *  - `isAccessibleForFree` and `offers price 0` are true only while no compensation exists
 *    (Section 17.3). Remove both the day any provider payment, referral fee or paid placement exists.
 */

/** Organization entity, rendered once site-wide (App.js PublicShell). */
export function buildOrganizationJsonLd() {
  return {
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.svg`,
    description:
      "RateCurrent is an educational website that helps Texans look up their ESID and understand their electric bill.",
    areaServed: { "@type": "State", name: "Texas" },
    contactPoint: {
      "@type": "ContactPoint",
      email: "support@ratecurrent.com",
      contactType: "customer support",
    },
  };
}

/** WebSite entity (site-wide, distinct from Organization per schema.org convention). */
export function buildWebSiteJsonLd() {
  return {
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: SITE_URL,
    name: SITE_NAME,
    description: "Free Texas ESID lookup and plain-language electric bill explainer.",
    inLanguage: "en-US",
    publisher: { "@id": `${SITE_URL}/#organization` },
  };
}

/** Combined site-wide graph: Organization + WebSite, one script tag. */
export function buildSiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@graph": [buildOrganizationJsonLd(), buildWebSiteJsonLd()],
  };
}

function buildWebApplication({ name, path, description }) {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name,
    url: `${SITE_URL}${path}`,
    applicationCategory: "UtilitiesApplication",
    operatingSystem: "Any (web browser)",
    description,
    areaServed: { "@type": "State", name: "Texas" },
    isAccessibleForFree: true,
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    provider: { "@id": `${SITE_URL}/#organization` },
  };
}

/** Page-level markup for /texas/esid-lookup only. */
export function buildEsidLookupJsonLd() {
  return buildWebApplication({
    name: "RateCurrent ESID Lookup",
    path: "/texas/esid-lookup",
    description: "Look up the Electric Service Identifier (ESID) for a Texas service address using ERCOT's public registry data.",
  });
}

/** Page-level markup for /bill-check only. */
export function buildBillCheckJsonLd() {
  return buildWebApplication({
    name: "RateCurrent Bill Check",
    path: "/bill-check",
    description:
      "Upload an electric bill to get an automated, plain-language explanation of the rate, usage, and charges on it. Automated results can contain errors.",
  });
}

/* ---- Answer pages (/help/<slug>), SEO spec 3.4 (d)(e)(f), answer_pages/README.md "File Format" item 2 ---- */

const orgRef = () => ({ "@type": "Organization", "@id": `${SITE_URL}/#organization`, name: SITE_NAME });

/**
 * Article: author and publisher are the Organization node only (no people, no reviewedBy); citation = the URLs in the page's visible Sources list (front-matter citations omit some on pages 3 and 6)
 * source URLs; dateModified = the visible "Last reviewed" date. No datePublished: no publish date is shown on the page.
 */
export function buildArticleJsonLd(page, dateModified) {
  const url = `${SITE_URL}${page.route}`;
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: page.h1,
    description: page.metaDescription,
    url,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    inLanguage: "en-US",
    dateModified,
    author: orgRef(),
    publisher: orgRef(),
    citation: page.sources.map((s) => s.url),
  };
}

/** FAQPage built from the SAME strings the page renders (plain-text view of the visible Common Questions). */
export function buildFaqPageJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((q) => ({
      "@type": "Question",
      name: q.name,
      acceptedAnswer: { "@type": "Answer", text: q.text },
    })),
  };
}

/** BreadcrumbList; only used on pages that also render a visible breadcrumb nav. `trail` = [{ name, url }] with site-relative urls. */
export function buildBreadcrumbJsonLd(trail) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((t, i) => ({ "@type": "ListItem", position: i + 1, name: t.name, item: `${SITE_URL}${t.url === "/" ? "/" : t.url}` })),
  };
}

/**
 * AboutPage for /about (CLO ruling 5: AboutPage plus the existing Organization node; no foundingDate, sameAs,
 * aggregateRating, review, award, slogan; no legalName until the footer shows the LLC name). `dateModified` is the
 * visible "Last updated" date.
 */
export function buildAboutPageJsonLd(about, dateModified) {
  const url = `${SITE_URL}${about.route}`;
  return {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: about.h1,
    description: about.metaDescription,
    url,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    inLanguage: "en-US",
    dateModified,
    isPartOf: { "@id": `${SITE_URL}/#website` },
    about: { "@id": `${SITE_URL}/#organization` },
    publisher: orgRef(),
  };
}
